import { useEffect, useRef, useState } from "react";
import { Bus, Upload, Trash2, AlertTriangle, Check, FileSpreadsheet, Wrench } from "lucide-react";
import * as XLSX from "xlsx";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Late-Arrival Roles panel — bus drivers, counselors, subs, and the new
 * "Staff" (staff who take the bus). These staff arrive after the regular
 * absent cutoff so we give them an extended Late threshold (currently 9:45).
 *
 * Upload format (xlsx / csv) — three columns:
 *   Last Name | First Name | Position
 *
 * Position must be one of: Bus Driver, Counselor, Sub, Staff
 *
 * Uploads are ADDITIVE — never wipes the existing list. Use "Clear All" or
 * the per-row Remove button to take staff off.
 */
const ALLOWED_TYPES = ["Bus Driver", "Counselor", "Sub", "Staff"];

export default function LateArrivalPanel() {
  const [items, setItems] = useState([]);
  const [threshold, setThreshold] = useState("09:45");
  const [absentThreshold, setAbsentThreshold] = useState("10:00");
  const [loading, setLoading] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const [invalidPos, setInvalidPos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/late-arrivals");
      setItems(r.data.items || []);
      setThreshold(r.data.late_arrival_threshold_local || "09:45");
      setAbsentThreshold(r.data.absent_threshold_local || "10:00");
    } catch { toast.error("Failed to load late-arrival list"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const sheet = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const keys = sheet.length ? Object.keys(sheet[0]) : [];
      const lastKey = keys.find((k) => /last/i.test(k));
      const firstKey = keys.find((k) => /first/i.test(k));
      const posKey = keys.find((k) => /position|type/i.test(k));
      if (!lastKey || !firstKey || !posKey) {
        toast.error('Need columns "Last Name", "First Name", "Position". Try the template.');
        return;
      }
      const rows = sheet
        .map((r) => ({
          last_name: String(r[lastKey] || "").trim(),
          first_name: String(r[firstKey] || "").trim(),
          position: String(r[posKey] || "").trim(),
        }))
        .filter((r) => r.last_name && r.first_name);
      if (!rows.length) {
        toast.error("No usable rows in the spreadsheet.");
        return;
      }
      if (!confirm(`Add ${rows.length} staff to the late-arrival list?\n\nThis is ADDITIVE — staff already on the list stay on it.`)) return;
      const res = await api.post("/admin/late-arrivals/bulk", { rows });
      setUnmatched(res.data.unmatched || []);
      setInvalidPos(res.data.invalid_position || []);
      const bits = [`Added ${res.data.set_count}.`];
      if (res.data.unmatched?.length) bits.push(`${res.data.unmatched.length} not in roster.`);
      if (res.data.invalid_position?.length) bits.push(`${res.data.invalid_position.length} invalid position.`);
      toast.success(bits.join(" "));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeOne = async (crew_id, name) => {
    if (!confirm(`Remove ${name} from the late-arrival list?`)) return;
    try {
      await api.delete(`/admin/late-arrivals/${crew_id}`);
      toast.success(`${name} removed`);
      load();
    } catch { toast.error("Remove failed"); }
  };

  const clearAll = async () => {
    if (!confirm(`Clear ALL ${items.length} late-arrival entries?`)) return;
    try {
      await api.post("/admin/late-arrivals/clear");
      toast.success("Late-arrival list cleared");
      setUnmatched([]);
      setInvalidPos([]);
      load();
    } catch { toast.error("Clear failed"); }
  };

  const [reclassifying, setReclassifying] = useState(false);
  const runReclassify = async () => {
    // Dry-run first so the admin knows what they're about to change.
    let pv;
    try {
      pv = await api.post("/admin/late-arrivals/reclassify", { dry_run: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
      return;
    }
    const willFlip = pv?.data?.would_flip ?? 0;
    const considered = pv?.data?.considered ?? 0;
    if (!willFlip) {
      toast.info(`Nothing to fix — checked ${considered} record${considered === 1 ? "" : "s"} in ${pv.data.start_date}…${pv.data.end_date}.`);
      return;
    }
    if (!confirm(
      `Fix ${willFlip} historical sign-in${willFlip === 1 ? "" : "s"}?\n\n` +
      `Bus-list staff whose sign-in was before ${pv.data.threshold_local} but were tagged Late/Late Arrival ` +
      `between ${pv.data.start_date} and ${pv.data.end_date} will be flipped to Present.\n\n` +
      `This re-enables them for the raffle. This action is safe to re-run.`
    )) return;
    setReclassifying(true);
    try {
      const r = await api.post("/admin/late-arrivals/reclassify", { dry_run: false });
      toast.success(`Fixed ${r.data.flipped} historical record${r.data.flipped === 1 ? "" : "s"} → Present.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fix failed");
    } finally {
      setReclassifying(false);
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Last Name", "First Name", "Position"],
      ["Doe", "Jane", "Bus Driver"],
      ["Smith", "John", "Counselor"],
      ["Brown", "Alex", "Sub"],
      ["Garcia", "Maria", "Staff"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Late Arrivals");
    XLSX.writeFile(wb, "rrdc-late-arrivals-template.xlsx");
  };

  const grouped = items.reduce((acc, m) => {
    const k = m.late_arrival_type || "Unspecified";
    acc[k] = acc[k] || [];
    acc[k].push(m);
    return acc;
  }, {});

  return (
    <section className="bg-amber-50/30 p-6" data-testid="late-arrival-section">
      <div className="flex items-center gap-2 mb-1">
        <Bus className="w-5 h-5 text-amber-700" />
        <h2 className="font-display text-lg font-bold">Late-Arrival Roles</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Bus drivers, counselors, subs, and staff who take the bus. These four
        types arrive after the regular 9:05 bus drop-off and are <strong>not</strong>
        considered late until <strong>{threshold}</strong>. They use Staff
        Check-In normally — the dashboard just gives them this later cutoff.
      </p>
      <p className="text-xs text-slate-500 mb-4">
        Upload format (3 columns): <code className="font-mono bg-white px-1 rounded">Last Name</code> · <code className="font-mono bg-white px-1 rounded">First Name</code> · <code className="font-mono bg-white px-1 rounded">Position</code>.
        Position must be one of: <strong>{ALLOWED_TYPES.join(", ")}</strong>. Uploads are additive.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
          data-testid="late-arrival-upload-btn"
        >
          <Upload className="w-4 h-4" /> Upload Spreadsheet (additive)
        </button>
        <input
          ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          onChange={handleFile} data-testid="late-arrival-file-input"
        />
        <button
          onClick={downloadTemplate}
          className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold"
          data-testid="late-arrival-template-btn"
        >
          <FileSpreadsheet className="w-4 h-4" /> Download Template
        </button>
        {items.length > 0 && (
          <button
            onClick={clearAll}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-white border border-red-200 hover:bg-red-50 text-red-700 font-semibold"
            data-testid="late-arrival-clear-btn"
          >
            <Trash2 className="w-4 h-4" /> Clear All
          </button>
        )}
        {items.length > 0 && (
          <button
            onClick={runReclassify}
            disabled={reclassifying}
            className="btn-touch inline-flex items-center gap-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold"
            data-testid="late-arrival-reclassify-btn"
            title={`Flip any Late/Late Arrival back to Present for bus-list staff who signed in before ${threshold}, from 2026-06-29 to 2026-08-20.`}
          >
            <Wrench className="w-4 h-4" /> {reclassifying ? "Fixing…" : "Fix Historical Records"}
          </button>
        )}
      </div>

      {unmatched.length > 0 && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 mb-4" data-testid="late-arrival-unmatched">
          <div className="flex items-center gap-2 mb-1 text-red-800 font-semibold text-sm">
            <AlertTriangle className="w-4 h-4" /> {unmatched.length} row{unmatched.length === 1 ? "" : "s"} didn&apos;t match the roster
          </div>
          <div className="text-xs text-red-700">Fix the spelling in your spreadsheet and re-upload, or add the staff to CampMinder first.</div>
          <ul className="mt-2 text-sm text-red-900 list-disc list-inside">
            {unmatched.slice(0, 30).map((n, i) => (
              <li key={i}>{n.last_name}, {n.first_name} ({n.position})</li>
            ))}
          </ul>
        </div>
      )}

      {invalidPos.length > 0 && (
        <div className="rounded-xl bg-amber-100 border border-amber-300 px-4 py-3 mb-4" data-testid="late-arrival-invalid-position">
          <div className="flex items-center gap-2 mb-1 text-amber-900 font-semibold text-sm">
            <AlertTriangle className="w-4 h-4" /> {invalidPos.length} row{invalidPos.length === 1 ? "" : "s"} had an unrecognized Position
          </div>
          <div className="text-xs text-amber-900">Position must be one of: {ALLOWED_TYPES.join(", ")}.</div>
          <ul className="mt-2 text-sm text-amber-900 list-disc list-inside">
            {invalidPos.slice(0, 30).map((n, i) => (
              <li key={i}>{n.last_name}, {n.first_name} → got &quot;{n.position}&quot;</li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl bg-white border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="late-arrival-empty">
          No late-arrival roles set yet. Upload a spreadsheet to get started.
        </div>
      ) : (
        <div className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid="late-arrival-list">
          <div className="px-4 py-2 bg-amber-100/60 border-b border-amber-200 text-xs uppercase tracking-wide font-bold text-amber-900 flex items-center gap-2">
            <Check className="w-3.5 h-3.5" /> {items.length} staff get the {threshold} Late cutoff
          </div>
          {Object.keys(grouped).sort().map((typeName) => (
            <div key={typeName} className="border-t first:border-t-0 border-slate-100">
              <div className="px-4 py-1.5 bg-slate-50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                {typeName} <span className="text-slate-400 ml-1">({grouped[typeName].length})</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {grouped[typeName].map((m) => (
                  <li key={m.crew_id} className="px-4 py-2 flex items-center gap-3" data-testid={`late-arrival-row-${m.crew_id}`}>
                    <Bus className="w-4 h-4 text-amber-600" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                      <div className="text-xs text-slate-500 truncate">{m.position || m.division || "—"}{m.phone ? ` · ····${String(m.phone).slice(-4)}` : ""}</div>
                    </div>
                    <button onClick={() => removeOne(m.crew_id, m.name)}
                            className="text-xs text-slate-400 hover:text-red-700"
                            data-testid={`late-arrival-remove-${m.crew_id}`}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
